import type CK from '~/types/contentking';
import GraphQL from '~/types/graphql';

import {
	useWebsiteLogFileAnalysisSetupQuery,
} from './useWebsiteLogFileAnalysisSetup.gql';

import useBatchContextWebsiteConfiguration from '~/hooks/useBatchContextWebsiteConfiguration';

import createKeyedStore from '~/createKeyedStore';



type ReturnType = {
	hasAvailableLogSources: boolean,
	isLoading: boolean,
	isLogFileAnalysisEnabled: boolean,
	isMissingLogSource: boolean,
	logSources: ReadonlyArray<{
		provider: GraphQL.LogSourceProvider,
		status: GraphQL.LogSourceStatus,
	}>,
	getStatusForLogSource: (logSource: GraphQL.LogSourceProvider) => GraphQL.LogSourceStatus,
};



const useLogFileAnalysisSetupStore = createKeyedStore<CK.WebsiteId, ReturnType>(
	() => ({
		getStatusForLogSource: () => GraphQL.LogSourceStatus.NotAvailable,
		hasAvailableLogSources: false,
		isLoading: false,
		isLogFileAnalysisEnabled: false,
		isMissingLogSource: false,
		logSources: [],
	}),
);

function useWebsiteLogFileAnalysisSetup(websiteId: CK.WebsiteId | null): ReturnType {
	const store = useLogFileAnalysisSetupStore(websiteId ?? '-1');

	useWebsiteLogFileAnalysisSetupQuery({
		context: useBatchContextWebsiteConfiguration(websiteId),
		skip: websiteId === null,
		variables: {
			websiteId: websiteId ?? '',
		},
		onCompleted: (data) => {
			if (data.website === null) {
				return;
			}

			const isLogFileAnalysisEnabled = data.website.isLogFileAnalysisEnabled;
			const logSources = data.website.logSources;

			let hasAvailableLogSources = false;
			let isMissingLogSource = isLogFileAnalysisEnabled;

			logSources
				.forEach((source) => {
					if (
						source.status === GraphQL.LogSourceStatus.Enabled
						|| source.status === GraphQL.LogSourceStatus.Available
					) {
						hasAvailableLogSources = true;
					}

					if (isMissingLogSource && source.status === GraphQL.LogSourceStatus.Enabled) {
						isMissingLogSource = false;
					}
				});

			function getStatusForLogSource(provider: GraphQL.LogSourceProvider) {
				const logSource = logSources.find((logSource) => logSource.provider === provider);

				return logSource?.status ?? GraphQL.LogSourceStatus.NotAvailable;
			}

			store.setProperty('isLoading', false);
			store.setProperty('hasAvailableLogSources', hasAvailableLogSources);
			store.setProperty('isLogFileAnalysisEnabled', isLogFileAnalysisEnabled);
			store.setProperty('isMissingLogSource', isMissingLogSource);
			store.setProperty('logSources', logSources);
			store.setProperty('getStatusForLogSource', () => getStatusForLogSource);
		},
	});

	return store;
}



export default useWebsiteLogFileAnalysisSetup;
